/**
 * agent-dm-stream-store
 *
 * Module-level store for the **ephemeral** state of an in-flight agent DM
 * response. Mirrors how iOS holds streaming thinking/content in
 * `AppModel.agentStream*` rather than in the database.
 *
 * Why this exists: previously the Tauri DM coworker streaming UX was
 * driven by writing thinking deltas to `messages.agent_thinking` every 50ms
 * via the scheduler placeholder pattern. That generates a Zero replication
 * storm for state that is fundamentally ephemeral, fires the unread trigger
 * before the answer exists, and creates two parallel state machines (Tauri
 * scheduler-driven vs iOS SSE-driven). This store unifies Tauri DM onto the
 * SSE path: thinking + content live here while the model is generating,
 * then the server posts the final message via the existing tee accumulator
 * and Zero replicates it normally.
 *
 * State is keyed by `channelId` because at most one streaming agent reply
 * is in flight per DM at a time.
 */

import { useCallback, useSyncExternalStore } from "react";

export interface AgentDmStreamState {
  /** Coworker user_id of the agent that's responding. */
  coworkerId: string;
  /** Current accumulated thinking text from `thinking_delta` events. */
  thinking: string;
  /** Current accumulated visible answer text from `assistant_delta` /
   *  `0:` deltas. */
  content: string;
  /** Tracks whether the SSE stream is still open. Flips false on
   *  finish/error/abort. The synthetic streaming bubble in the message
   *  list disappears when this is false (the real DB row replaces it). */
  isStreaming: boolean;
  /** Trigger message id (the user's just-sent message). Used as the
   *  React key on the synthetic bubble so a fresh send remounts it. */
  triggerMessageId: string;
  /** Wallclock when the stream began. Used for timeout cleanup and the
   *  skip-if-fast UX rule. */
  startedAt: number;
  /** Optional inline error if the stream failed mid-flight. */
  error?: string;
}

type Listener = () => void;

class AgentDmStreamStore {
  #byChannel = new Map<string, AgentDmStreamState>();
  #listeners = new Map<string, Set<Listener>>();
  #version = 0;

  /**
   * Begin a new streaming turn for a channel. Replaces any existing
   * entry — there can only be one in flight at a time.
   *
   * Concurrency: every mutation (`appendThinking`, `appendContent`,
   * `setContent`, `finish`, `fail`) takes the same `triggerMessageId`
   * the call to `start()` was given. If a second `start()` for the same
   * channel happens before the first finishes, the second's id wins,
   * and any straggler events from the first stream are silently
   * dropped (they no longer match the active turn). This prevents
   * delta-bleed between concurrent turns and prevents the first
   * stream's `finish()` from deleting the second's state.
   */
  start(
    channelId: string,
    init: { coworkerId: string; triggerMessageId: string },
  ): void {
    this.#byChannel.set(channelId, {
      coworkerId: init.coworkerId,
      thinking: "",
      content: "",
      isStreaming: true,
      triggerMessageId: init.triggerMessageId,
      startedAt: Date.now(),
    });
    this.#bump(channelId);
  }

  /** Append a thinking delta. No-op if no active turn matches the
   *  caller's `triggerMessageId` (a stale stream from a superseded turn). */
  appendThinking(
    channelId: string,
    triggerMessageId: string,
    delta: string,
  ): void {
    const cur = this.#byChannel.get(channelId);
    if (!cur || !cur.isStreaming) return;
    if (cur.triggerMessageId !== triggerMessageId) return;
    this.#byChannel.set(channelId, {
      ...cur,
      thinking: cur.thinking + delta,
    });
    this.#bump(channelId);
  }

  /** Append a content delta. No-op if the trigger doesn't match the
   *  active turn (stale stream from a superseded turn). */
  appendContent(
    channelId: string,
    triggerMessageId: string,
    delta: string,
  ): void {
    const cur = this.#byChannel.get(channelId);
    if (!cur || !cur.isStreaming) return;
    if (cur.triggerMessageId !== triggerMessageId) return;
    this.#byChannel.set(channelId, {
      ...cur,
      content: cur.content + delta,
    });
    this.#bump(channelId);
  }

  /** Replace content (used by `assistant_final` events). Trigger-gated. */
  setContent(
    channelId: string,
    triggerMessageId: string,
    content: string,
  ): void {
    const cur = this.#byChannel.get(channelId);
    if (!cur || !cur.isStreaming) return;
    if (cur.triggerMessageId !== triggerMessageId) return;
    this.#byChannel.set(channelId, { ...cur, content });
    this.#bump(channelId);
  }

  /**
   * Mark the stream as finished. The synthetic bubble will disappear
   * on the next render — the real DB row (already INSERTed by the
   * server-side tee accumulator) takes its place.
   *
   * Trigger-gated: a stale `finish()` from a superseded stream is a
   * no-op. Without this gate, the first stream's `finish()` would
   * delete the second stream's state on a rapid-resend.
   */
  finish(channelId: string, triggerMessageId: string): void {
    const cur = this.#byChannel.get(channelId);
    if (!cur) return;
    if (cur.triggerMessageId !== triggerMessageId) return;
    this.#byChannel.delete(channelId);
    this.#bump(channelId);
  }

  /** Mark an error. Trigger-gated like `finish`. */
  fail(channelId: string, triggerMessageId: string, error: string): void {
    const cur = this.#byChannel.get(channelId);
    if (!cur) return;
    if (cur.triggerMessageId !== triggerMessageId) return;
    this.#byChannel.set(channelId, { ...cur, isStreaming: false, error });
    this.#bump(channelId);
  }

  /**
   * Force-clear the entry for a channel regardless of which turn owns
   * it. Used by Zero replication observers when a real DB row arrives
   * for the channel — at that point the synthetic bubble must go away
   * even if the SSE close hasn't fired yet (network drop case).
   */
  forceClear(channelId: string): void {
    if (!this.#byChannel.has(channelId)) return;
    this.#byChannel.delete(channelId);
    this.#bump(channelId);
  }

  get(channelId: string): AgentDmStreamState | undefined {
    return this.#byChannel.get(channelId);
  }

  get version(): number {
    return this.#version;
  }

  subscribe(channelId: string, cb: Listener): () => void {
    let set = this.#listeners.get(channelId);
    if (!set) {
      set = new Set();
      this.#listeners.set(channelId, set);
    }
    set.add(cb);
    return () => {
      set!.delete(cb);
      if (set!.size === 0) this.#listeners.delete(channelId);
    };
  }

  #bump(channelId: string): void {
    this.#version++;
    const set = this.#listeners.get(channelId);
    if (set) for (const cb of set) cb();
  }
}

/** Singleton — there's only one agent stream store per app. */
export const agentDmStreamStore = new AgentDmStreamStore();

/** React hook — re-renders when the streaming state for `channelId`
 *  changes. Returns `undefined` when no stream is active. */
export function useAgentDmStream(
  channelId: string | null | undefined,
): AgentDmStreamState | undefined {
  const subscribe = useCallback(
    (cb: () => void) => {
      if (!channelId) return () => {};
      return agentDmStreamStore.subscribe(channelId, cb);
    },
    [channelId],
  );
  const getSnapshot = useCallback(
    () => (channelId ? agentDmStreamStore.get(channelId) : undefined),
    [channelId],
  );
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
